import { Settings } from "@/components/ui/settings";
import { useAuth } from "@/lib/auth";
import { openSystemBiometricSettings } from "@/lib/auth/biometricUnlock";
import { showAlert } from "@/lib/showAlert";
import { THEME, type ColorScheme } from "@/lib/theme";
import { TEST_IDS } from "@/testIdDefinition";
import { router } from "expo-router";
import { useColorScheme } from "nativewind";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "react-native";

export default function SecuritySettings() {
    const { t } = useTranslation();
    const { colorScheme } = useColorScheme();
    const scheme = (colorScheme ?? "light") as ColorScheme;
    const {
        isBiometricUnlockAvailable,
        isBiometricUnlockDisabled,
        hasBiometricHardware,
        biometricLabel,
        setBiometricUnlockDisabled,
    } = useAuth();
    const pendingEnableRef = useRef(false);
    const systemBlocked = hasBiometricHardware && !isBiometricUnlockAvailable;

    useEffect(() => {
        if (!pendingEnableRef.current || !isBiometricUnlockAvailable) {
            return;
        }
        pendingEnableRef.current = false;
        void setBiometricUnlockDisabled(false);
    }, [isBiometricUnlockAvailable, setBiometricUnlockDisabled]);

    const promptOpenSystemSettings = useCallback(() => {
        showAlert(
            t("settings.biometricOpenSettingsTitle", {
                method: biometricLabel,
            }),
            t("settings.biometricOpenSettingsMessage", {
                method: biometricLabel,
            }),
            [
                { text: t("ui.cancel"), variant: "outline" },
                {
                    text: t("settings.notifications.openSettings"),
                    testID: TEST_IDS[
                        TEST_IDS.SETTINGS_BIOMETRIC_OPEN_SETTINGS_BUTTON
                    ],
                    onPress: () => {
                        pendingEnableRef.current = true;
                        void openSystemBiometricSettings();
                    },
                },
            ],
        );
    }, [biometricLabel, t]);

    const onValueChange = useCallback(
        (enabled: boolean) => {
            if (!enabled) {
                pendingEnableRef.current = false;
                void setBiometricUnlockDisabled(true);
                return;
            }

            if (isBiometricUnlockAvailable) {
                void setBiometricUnlockDisabled(false);
                return;
            }

            promptOpenSystemSettings();
        },
        [
            isBiometricUnlockAvailable,
            promptOpenSystemSettings,
            setBiometricUnlockDisabled,
        ],
    );

    return (
        <Settings
            title={t("settings.security")}
            footer={
                systemBlocked
                    ? t("settings.biometricSystemDisabled", {
                          method: biometricLabel,
                      })
                    : undefined
            }
        >
            {hasBiometricHardware && (
                <Settings.Item
                    label={t("settings.requireUnlock", {
                        method: biometricLabel,
                    })}
                >
                    <Switch
                        value={
                            isBiometricUnlockAvailable &&
                            !isBiometricUnlockDisabled
                        }
                        onValueChange={onValueChange}
                        trackColor={{
                            false: THEME[scheme].border,
                            true: THEME[scheme].primary,
                        }}
                        thumbColor="#FFFFFF"
                    />
                </Settings.Item>
            )}
            <Settings.Item
                label={t("settings.activeSessions")}
                icon="laptop"
                onPress={() => router.push("/active-sessions")}
                testID={TEST_IDS[TEST_IDS.SETTINGS_ACTIVE_SESSIONS_LINK]}
                group
            />
        </Settings>
    );
}
